import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import LandingNavbar from '../components/LandingNavbar';

const PROG_ACCENTS = ['#1B2A5E','#2D3F7A','#C9A84C','#A07C28','#243570'];
const PROG_ICONS   = ['ri-briefcase-4-line','ri-scales-line','ri-global-line','ri-bar-chart-box-line','ri-earth-line'];

export default function LandingPage() {
  const navigate = useNavigate();
  const [programmes, setProgrammes] = useState([]);
  const [showBackTop, setShowBackTop] = useState(false);

  useEffect(() => {
    const onScroll = () => setShowBackTop(window.scrollY > 400);
    window.addEventListener('scroll', onScroll, { passive: true });

    api.get('/academic/programmes/public').then(r => setProgrammes(r.data)).catch(() => {});

    const reveals = document.querySelectorAll('.hu-reveal');
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('hu-visible'); observer.unobserve(e.target); } }),
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    reveals.forEach((el) => observer.observe(el));

    setTimeout(() => {
      document.querySelectorAll('.hu-hero .hu-reveal').forEach((el) => el.classList.add('hu-visible'));
    }, 100);

    return () => { window.removeEventListener('scroll', onScroll); observer.disconnect(); };
  }, []);

  return (
    <>
      <style>{`
        .hu-root*,.hu-root *::before,.hu-root *::after{box-sizing:border-box;margin:0;padding:0}
        .hu-root{font-family:'Inter',sans-serif;background:#F8FAFC;color:#0A1628;line-height:1.6;overflow-x:hidden;
          --navy:#1B2A5E;--navy-mid:#243570;--navy-light:#2D3F7A;--navy-elev:#4A5E9A;--navy-border:#C8D0E8;
          --gold:#C9A84C;--gold-light:#F5EDD4;--gold-dim:#A07C28;--gold-pale:rgba(201,168,76,.10);--gold-glow:rgba(201,168,76,.20);
          --text-primary:#0A1628;--text-secondary:#475569;--text-muted:#94A3B8;--success:#16A34A;--border:#E2E8F0;--bg:#F8FAFC;--bg-card:#FFFFFF}
        .hu-root a{text-decoration:none;color:inherit}
        .hu-root img{max-width:100%}
        .hu-root ul{list-style:none}
        .hu-container{max-width:1200px;margin:0 auto;padding:0 2rem}
        .hu-badge{display:inline-flex;align-items:center;gap:.4rem;background:var(--gold-pale);color:var(--gold);border:1px solid rgba(21,101,192,.2);font-size:.75rem;font-weight:600;letter-spacing:.05em;padding:.3rem .8rem;border-radius:99px}
        .hu-badge i{font-size:.9rem}
        .hu-section-label{display:inline-flex;align-items:center;gap:.5rem;font-size:.78rem;font-weight:700;letter-spacing:.12em;text-transform:uppercase;color:var(--gold);margin-bottom:1rem}
        .hu-section-label::before,.hu-section-label::after{content:'';width:24px;height:1px;background:rgba(21,101,192,.15)}
        h2.hu-section-title{font-family:'Inter',sans-serif;font-size:clamp(2rem,4vw,2.75rem);font-weight:700;line-height:1.15;margin-bottom:.75rem}
        h2.hu-section-title span{color:var(--gold)}
        p.hu-section-sub{color:var(--text-secondary);font-size:1.0625rem;max-width:560px}
        .hu-text-center{text-align:center}
        .hu-text-center .hu-section-sub{margin:0 auto}
        .hu-btn{display:inline-flex;align-items:center;gap:.5rem;font-family:inherit;font-size:.9375rem;font-weight:600;padding:.75rem 1.75rem;border-radius:12px;cursor:pointer;border:none;transition:all 200ms ease;white-space:nowrap}
        .hu-btn--gold{background:var(--gold);color:#FFF;box-shadow:0 4px 20px rgba(21,101,192,.25)}
        .hu-btn--gold:hover{background:var(--gold-light);transform:translateY(-2px);box-shadow:0 6px 28px rgba(21,101,192,.35)}
        .hu-btn--outline{background:transparent;color:#0A1628;border:1.5px solid var(--navy-border)}
        .hu-btn--outline:hover{border-color:var(--gold);color:var(--gold);transform:translateY(-2px)}
        .hu-btn--ghost{background:transparent;color:var(--text-secondary)}
        .hu-btn--ghost:hover{color:#0A1628}
        .hu-btn--lg{padding:.9rem 2.25rem;font-size:1rem;border-radius:14px}
        .hu-btn--sm{padding:.5rem 1.2rem;font-size:.875rem;border-radius:10px}
        .hu-hero{min-height:100vh;position:relative;overflow:hidden;display:flex;align-items:center;padding:8rem 0 5rem;background:#F8FAFC}
        .hu-hero__world{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;object-position:center;opacity:.13;pointer-events:none;user-select:none}
        .hu-orb{display:none}
        .hu-hero-grid{display:none}
        .hu-hero__inner{display:grid;grid-template-columns:1fr 1fr;gap:4rem;align-items:center;position:relative;z-index:1}
        .hu-hero__badge{margin-bottom:1.5rem}
        .hu-hero__headline{font-family:'Inter',sans-serif;font-size:clamp(2.5rem,5.5vw,4rem);font-weight:800;line-height:1.1;margin-bottom:1.25rem}
        .hu-hero__headline .line{display:block}
        .hu-hero__headline .gold-text{color:transparent;background:linear-gradient(135deg,#C9A84C 0%,#F5D878 50%,#C9A84C 100%);background-clip:text;-webkit-background-clip:text;-webkit-text-fill-color:transparent;animation:huShimmer 4s linear infinite;background-size:200% auto}
        @keyframes huShimmer{to{background-position:200% center}}
        .hu-hero__sub{color:var(--text-secondary);font-size:1.125rem;line-height:1.75;margin-bottom:2.25rem;max-width:480px}
        .hu-hero__actions{display:flex;align-items:center;gap:1rem;flex-wrap:wrap}
        .hu-hero__trust{display:flex;align-items:center;gap:.75rem;margin-top:2.5rem;padding-top:2rem;border-top:1px solid rgba(21,101,192,.12)}
        .hu-trust-avatars{display:flex}
        .hu-trust-avatars span{width:36px;height:36px;border-radius:50%;border:2.5px solid #fff;font-size:.72rem;font-weight:800;display:flex;align-items:center;justify-content:center;margin-left:-10px;box-shadow:0 2px 6px rgba(21,101,192,.18)}
        .hu-trust-avatars span:first-child{margin-left:0}
        .hu-trust-avatars span:nth-child(1){background:#1B2A5E;color:#fff}
        .hu-trust-avatars span:nth-child(2){background:#243570;color:#fff}
        .hu-trust-avatars span:nth-child(3){background:#C9A84C;color:#fff}
        .hu-trust-avatars span:nth-child(4){background:#2D3F7A;color:#fff}
        .hu-trust-avatars span:nth-child(5){background:#EEF0F8;color:#1B2A5E}
        .hu-trust-text{font-size:.875rem;color:var(--text-secondary)}
        .hu-trust-text strong{color:#0A1628}
        .hu-hero__visual{position:relative;display:flex;align-items:stretch}
        .hu-image-frame{border-radius:24px;overflow:hidden;box-shadow:0 40px 100px rgba(27,42,94,.18),0 0 0 3px rgba(201,168,76,.2);line-height:0;width:100%;max-width:500px;aspect-ratio:3/4;margin:0 auto}
        .hu-image-frame img{width:100%;height:100%;display:block;object-fit:cover;object-position:center top}
        .hu-float{position:absolute;background:#FFF;border:1px solid var(--navy-border);border-radius:14px;padding:.75rem 1rem;box-shadow:0 2px 12px rgba(21,101,192,.08);display:flex;align-items:center;gap:.6rem;white-space:nowrap}
        .hu-float--cert{bottom:-16px;left:-40px;animation:huFloat 5s ease-in-out infinite}
        .hu-float--session{top:-16px;right:-24px;animation:huFloat 6s ease-in-out infinite reverse}
        @keyframes huFloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-8px)}}
        .hu-float-icon{width:36px;height:36px;border-radius:10px;display:flex;align-items:center;justify-content:center;font-size:1.1rem}
        .hu-float-icon--gold{background:rgba(21,101,192,.10);color:var(--gold)}
        .hu-float-icon--blue{background:rgba(59,130,246,.12);color:#3B82F6}
        .hu-float-label{font-size:.7rem;color:var(--text-muted)}
        .hu-float-value{font-size:.875rem;font-weight:700}
        .hu-stats-bar{background:#FFF;border-top:1px solid var(--navy-border);border-bottom:1px solid var(--navy-border);padding:2.5rem 0}
        .hu-stats-bar__grid{display:grid;grid-template-columns:repeat(4,1fr)}
        .hu-stats-bar__item{text-align:center;padding:0 1.5rem;border-right:1px solid var(--navy-border)}
        .hu-stats-bar__item:last-child{border-right:none}
        .hu-stats-bar__num{font-size:2.25rem;font-weight:800;color:var(--gold);line-height:1.1;margin-bottom:.25rem}
        .hu-stats-bar__label{font-size:.875rem;color:var(--text-secondary)}
        .hu-features{padding:6rem 0}
        .hu-features__grid{display:grid;grid-template-columns:repeat(3,1fr);gap:1.5rem;margin-top:3.5rem}
        .hu-feat-card{background:#FFF;border:1px solid var(--navy-border);border-radius:20px;padding:2rem;transition:all 250ms ease;position:relative;overflow:hidden}
        .hu-feat-card::before{content:'';position:absolute;inset:0;border-radius:inherit;background:linear-gradient(135deg,var(--gold-pale) 0%,transparent 60%);opacity:0;transition:opacity 300ms ease}
        .hu-feat-card:hover{border-color:rgba(21,101,192,.15);transform:translateY(-4px);box-shadow:0 12px 40px rgba(21,101,192,.06)}
        .hu-feat-card:hover::before{opacity:1}
        .hu-feat-card--highlight{background:rgba(255,255,255,.75);backdrop-filter:blur(16px);border-color:rgba(21,101,192,.2);box-shadow:0 4px 24px rgba(21,101,192,.08)}
        .hu-feat-card__icon{width:52px;height:52px;border-radius:14px;background:var(--gold-pale);color:var(--gold);display:flex;align-items:center;justify-content:center;font-size:1.5rem;margin-bottom:1.25rem;position:relative;z-index:1}
        .hu-feat-card--b .hu-feat-card__icon{background:rgba(27,42,94,.12);color:#1B2A5E}
        .hu-feat-card--c .hu-feat-card__icon{background:rgba(201,168,76,.12);color:#A07C28}
        .hu-feat-card--d .hu-feat-card__icon{background:rgba(27,42,94,.1);color:#2D3F7A}
        .hu-feat-card--e .hu-feat-card__icon{background:rgba(201,168,76,.1);color:#C9A84C}
        .hu-feat-card--f .hu-feat-card__icon{background:rgba(27,42,94,.08);color:#1B2A5E}
        .hu-feat-card__title{font-size:1.0625rem;font-weight:700;margin-bottom:.5rem;position:relative;z-index:1}
        .hu-feat-card__text{font-size:.9rem;color:var(--text-secondary);line-height:1.65;position:relative;z-index:1}
        .hu-how{padding:6rem 0;background:#FFF}
        .hu-how__inner{display:grid;grid-template-columns:1fr 1fr;gap:5rem;align-items:center}
        .hu-how__steps{display:flex;flex-direction:column;gap:0;margin-top:2.5rem}
        .hu-step{display:flex;gap:1.25rem;padding:1.75rem;border-radius:16px;transition:all 200ms}
        .hu-step:hover{background:#F8FAFC}
        .hu-step__num{width:44px;height:44px;border-radius:12px;flex-shrink:0;background:var(--gold-pale);color:var(--gold);font-size:.9rem;font-weight:800;display:flex;align-items:center;justify-content:center;border:1px solid rgba(21,101,192,.2)}
        .hu-step__title{font-weight:700;margin-bottom:.4rem}
        .hu-step__text{font-size:.9rem;color:var(--text-secondary);line-height:1.65}
        .hu-how__visual{position:relative;display:flex;justify-content:center}
        .hu-dashboard{background:#F8FAFC;border:1px solid var(--navy-border);border-radius:24px;overflow:hidden;width:100%;max-width:440px;box-shadow:0 40px 80px rgba(0,0,0,.12)}
        .hu-dash-header{background:#FFF;padding:1rem 1.25rem;display:flex;align-items:center;gap:.75rem;border-bottom:1px solid var(--navy-border)}
        .hu-dash-avatar{width:36px;height:36px;border-radius:50%;background:var(--gold);color:#FFF;font-size:.85rem;font-weight:700;display:flex;align-items:center;justify-content:center}
        .hu-dash-name{font-weight:600;font-size:.9rem}
        .hu-dash-role{font-size:.75rem;color:var(--text-muted)}
        .hu-dash-notif{margin-left:auto;width:34px;height:34px;border-radius:10px;background:#FFF;display:flex;align-items:center;justify-content:center;position:relative;font-size:1.1rem;color:var(--text-secondary)}
        .hu-dash-notif span{position:absolute;top:4px;right:4px;width:8px;height:8px;background:var(--gold);border-radius:50%;border:2px solid #FFF}
        .hu-dash-body{padding:1.25rem}
        .hu-dash-greeting{font-weight:700;margin-bottom:1rem}
        .hu-stat-row{display:grid;grid-template-columns:repeat(3,1fr);gap:.75rem;margin-bottom:1.25rem}
        .hu-stat{background:#FFF;border-radius:12px;padding:.875rem;border:1px solid var(--navy-border)}
        .hu-stat-val{font-weight:800;font-size:1.25rem;color:var(--gold)}
        .hu-stat-key{font-size:.72rem;color:var(--text-muted)}
        .hu-prog-list{display:flex;flex-direction:column;gap:.6rem}
        .hu-prog-row{background:#FFF;border-radius:10px;padding:.75rem;display:flex;align-items:center;gap:.75rem;border:1px solid var(--navy-border)}
        .hu-prog-dot{width:8px;height:8px;border-radius:50%;flex-shrink:0}
        .hu-prog-title{font-size:.82rem;font-weight:600;flex:1}
        .hu-prog-bar-wrap{width:60px;height:3px;background:#E2E8F0;border-radius:2px}
        .hu-prog-bar{height:100%;border-radius:2px;background:var(--gold)}
        .hu-prog-num{font-size:.75rem;color:var(--text-muted);white-space:nowrap}
        .hu-programmes{padding:6rem 0}
        .hu-programmes__header{display:flex;align-items:flex-end;justify-content:space-between;margin-bottom:3rem;gap:1rem}
        .hu-programmes__grid{display:grid;grid-template-columns:repeat(4,1fr);gap:1.5rem}
        @media(max-width:1100px){.hu-programmes__grid{grid-template-columns:repeat(2,1fr)}}
        @media(max-width:580px){.hu-programmes__grid{grid-template-columns:1fr}}
        .hu-prog-card{background:#FFF;border:1px solid #E2EEFF;border-radius:16px;overflow:hidden;transition:all 250ms ease;cursor:pointer;display:flex;flex-direction:column}
        .hu-prog-card:hover{transform:translateY(-5px);box-shadow:0 12px 40px rgba(21,101,192,.12);border-color:rgba(21,101,192,.3)}
        .hu-prog-card__accent{height:5px}
        .hu-prog-card__header{padding:1.25rem 1.25rem .75rem;display:flex;align-items:center;gap:.875rem}
        .hu-prog-card__icon-wrap{width:44px;height:44px;border-radius:11px;display:flex;align-items:center;justify-content:center;flex-shrink:0;font-size:1.3rem}
        .hu-prog-card__meta-top{display:flex;flex-direction:column;gap:.2rem}
        .hu-prog-card__code{font-size:.7rem;font-weight:700;text-transform:uppercase;letter-spacing:.08em}
        .hu-prog-card__level{font-size:.68rem;font-weight:600;padding:.15rem .45rem;border-radius:4px}
        .hu-prog-card__body{padding:.25rem 1.25rem 1.25rem;flex:1;display:flex;flex-direction:column}
        .hu-prog-card__title{font-weight:700;font-size:.975rem;color:#1B2A5E;line-height:1.4;margin-bottom:.5rem}
        .hu-prog-card__desc{font-size:.8rem;color:#64748B;line-height:1.6;margin-bottom:.875rem;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;flex:1}
        .hu-prog-card__stats{display:flex;gap:1.1rem;font-size:.78rem;color:#94A3B8;margin-bottom:1rem}
        .hu-prog-card__stats span{display:flex;align-items:center;gap:.3rem}
        .hu-prog-card__footer{display:flex;align-items:center;justify-content:space-between;padding-top:.875rem;border-top:1px solid #F1F5F9;margin-top:auto}
        .hu-prog-card__price{font-weight:800;font-size:1.05rem}
        .hu-prog-card__price small{font-size:.72rem;font-weight:400;color:#94A3B8}
        .hu-prog-card__cta{font-size:.78rem;font-weight:600;padding:.4rem .9rem;border-radius:7px;border:none;cursor:pointer;color:#FFF;transition:opacity 150ms}
        .hu-prog-card__cta:hover{opacity:.88}
        .hu-back-top{position:fixed;bottom:2rem;right:2rem;width:44px;height:44px;border-radius:50%;background:#1B2A5E;color:#FFF;border:none;cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:1.1rem;box-shadow:0 4px 16px rgba(27,42,94,.35);transition:all 250ms ease;z-index:999}
        .hu-back-top:hover{background:#152248;transform:translateY(-2px)}
        .hu-roles{padding:6rem 0;background:#FFF}
        .hu-roles__grid{display:grid;grid-template-columns:repeat(5,1fr);gap:1rem;margin-top:3.5rem}
        .hu-role-card{background:#F8FAFC;border:1px solid var(--navy-border);border-radius:16px;padding:1.5rem 1.25rem;text-align:center;transition:all 250ms ease}
        .hu-role-card:hover{border-color:rgba(21,101,192,.15);transform:translateY(-4px)}
        .hu-role-card__icon{width:56px;height:56px;border-radius:16px;margin:0 auto 1rem;display:flex;align-items:center;justify-content:center;font-size:1.6rem}
        .hu-role-card:nth-child(1) .hu-role-card__icon{background:var(--gold-pale);color:var(--gold)}
        .hu-role-card:nth-child(2) .hu-role-card__icon{background:rgba(21,101,192,.12);color:#1976D2}
        .hu-role-card:nth-child(3) .hu-role-card__icon{background:rgba(13,71,161,.1);color:#0D47A1}
        .hu-role-card:nth-child(4) .hu-role-card__icon{background:rgba(30,136,229,.12);color:#1E88E5}
        .hu-role-card:nth-child(5) .hu-role-card__icon{background:rgba(2,136,209,.1);color:#0288D1}
        .hu-role-card__title{font-weight:700;font-size:.9375rem;margin-bottom:.5rem}
        .hu-role-card__text{font-size:.82rem;color:var(--text-secondary);line-height:1.6}
        .hu-testimonials{padding:6rem 0}
        .hu-testimonials__grid{display:grid;grid-template-columns:repeat(3,1fr);gap:1.5rem;margin-top:3.5rem}
        .hu-testi-card{background:#FFF;border:1px solid var(--navy-border);border-radius:20px;padding:2rem;position:relative;transition:all 250ms ease}
        .hu-testi-card:hover{border-color:rgba(21,101,192,.2);transform:translateY(-3px)}
        .hu-testi-card__quote{font-size:2.5rem;color:var(--gold);opacity:.5;line-height:1;margin-bottom:.5rem}
        .hu-testi-card__text{font-size:.9375rem;color:var(--text-secondary);line-height:1.75;margin-bottom:1.5rem}
        .hu-testi-card__author{display:flex;align-items:center;gap:.875rem}
        .hu-testi-card__avatar{width:44px;height:44px;border-radius:50%;font-weight:700;font-size:.875rem;display:flex;align-items:center;justify-content:center;flex-shrink:0}
        .hu-testi-card:nth-child(1) .hu-testi-card__avatar{background:var(--gold-pale);color:var(--gold)}
        .hu-testi-card:nth-child(2) .hu-testi-card__avatar{background:rgba(27,42,94,.15);color:#1B2A5E}
        .hu-testi-card:nth-child(3) .hu-testi-card__avatar{background:rgba(201,168,76,.12);color:#A07C28}
        .hu-testi-card__name{font-weight:700;font-size:.9rem}
        .hu-testi-card__role{font-size:.8rem;color:var(--text-muted)}
        .hu-testi-card__stars{display:flex;gap:.15rem;margin-left:auto}
        .hu-testi-card__stars i{color:var(--gold);font-size:.875rem}
        .hu-cta{padding:5rem 0}
        .hu-cta__inner{background:rgba(255,255,255,.75);backdrop-filter:blur(20px);border:1px solid rgba(21,101,192,.15);border-radius:28px;padding:4rem;text-align:center;position:relative;overflow:hidden}
        .hu-cta__inner::before{content:'';position:absolute;inset:0;background:radial-gradient(ellipse 60% 80% at 50% 0%,rgba(21,101,192,.06) 0%,transparent 60%);pointer-events:none}
        .hu-cta__title{font-size:clamp(2rem,4vw,2.75rem);font-weight:700;margin-bottom:.875rem;position:relative;z-index:1}
        .hu-cta__title span{color:var(--gold)}
        .hu-cta__sub{color:var(--text-secondary);font-size:1.0625rem;max-width:520px;margin:0 auto 2.5rem;position:relative;z-index:1}
        .hu-cta__actions{display:flex;align-items:center;justify-content:center;gap:1rem;flex-wrap:wrap;position:relative;z-index:1}
        .hu-cta__note{margin-top:1.25rem;font-size:.82rem;color:var(--text-muted);position:relative;z-index:1}
        .hu-cta__note i{color:var(--success)}
        .hu-footer{background:#FFF;border-top:1px solid var(--navy-border);padding:4rem 0 2rem}
        .hu-footer__grid{display:grid;grid-template-columns:2fr 1fr 1fr 1fr;gap:3rem;margin-bottom:3rem}
        .hu-footer__brand-text{font-size:.9rem;color:var(--text-secondary);line-height:1.7;margin:1rem 0 1.25rem;max-width:280px}
        .hu-footer__socials{display:flex;gap:.5rem}
        .hu-footer__social{width:36px;height:36px;border-radius:10px;background:#FFF;border:1px solid var(--navy-border);display:flex;align-items:center;justify-content:center;font-size:1rem;color:var(--text-secondary);transition:all 180ms ease}
        .hu-footer__social:hover{border-color:var(--gold);color:var(--gold)}
        .hu-footer__col-title{font-size:.8rem;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:var(--text-muted);margin-bottom:1.25rem}
        .hu-footer__links{display:flex;flex-direction:column;gap:.625rem}
        .hu-footer__links a{font-size:.9rem;color:var(--text-secondary);transition:color 180ms}
        .hu-footer__links a:hover{color:var(--gold)}
        .hu-footer__bottom{padding-top:2rem;border-top:1px solid var(--navy-border);display:flex;align-items:center;justify-content:space-between;gap:1rem;flex-wrap:wrap}
        .hu-footer__copy{font-size:.85rem;color:var(--text-muted)}
        .hu-footer__copy span{color:var(--gold)}
        .hu-footer__bottom-links{display:flex;gap:1.5rem}
        .hu-footer__bottom-links a{font-size:.85rem;color:var(--text-muted);transition:color 180ms}
        .hu-footer__bottom-links a:hover{color:var(--text-secondary)}
        .hu-reveal{opacity:0;transform:translateY(32px);transition:opacity 600ms ease,transform 600ms ease}
        .hu-reveal.hu-visible{opacity:1;transform:translateY(0)}
        .hu-d1{transition-delay:100ms}.hu-d2{transition-delay:200ms}.hu-d3{transition-delay:300ms}.hu-d4{transition-delay:400ms}.hu-d5{transition-delay:500ms}
        @keyframes huHeroReveal{to{opacity:1;transform:none}}
        .hu-hero .hu-d1{animation:huHeroReveal .65s ease .05s both}
        .hu-hero .hu-d2{animation:huHeroReveal .65s ease .15s both}
        .hu-hero .hu-d3{animation:huHeroReveal .65s ease .25s both}
        .hu-hero .hu-d4{animation:huHeroReveal .65s ease .35s both}
        @media(max-width:1100px){.hu-features__grid{grid-template-columns:repeat(2,1fr)}.hu-programmes__grid{grid-template-columns:repeat(2,1fr)}}
        .hu-hero__visual--mobile{display:none}
        @media(max-width:900px){
          .hu-hero{padding:6rem 0 3.5rem}
          .hu-hero__inner{grid-template-columns:1fr;text-align:center;gap:2rem}
          .hu-hero__actions{justify-content:center;flex-wrap:wrap}
          .hu-hero__trust{justify-content:center}
          .hu-hero__sub{margin-left:auto;margin-right:auto}
          .hu-hero__visual{display:none}
          .hu-hero__visual--mobile{display:flex;justify-content:center;margin:1.5rem 0}
          .hu-image-frame{max-width:300px;aspect-ratio:4/3}
          .hu-how__inner{grid-template-columns:1fr}
          .hu-how__visual{display:none}
          .hu-programmes__grid{grid-template-columns:repeat(2,1fr)}
          .hu-testimonials__grid{grid-template-columns:1fr}
          .hu-testimonials__grid .hu-testi-card:nth-child(3){display:none}
          .hu-footer__grid{grid-template-columns:1fr 1fr;gap:2rem}
          .hu-stats-bar__grid{grid-template-columns:repeat(2,1fr)}
          .hu-stats-bar__item:nth-child(2){border-right:none}
          .hu-stats-bar__item{padding:1rem 1.5rem}
        }
        @media(max-width:680px){
          .hu-hero{padding:5rem 0 3rem}
          .hu-image-frame{max-width:260px;aspect-ratio:4/3}
          .hu-btn--lg{padding:.8rem 1.5rem;font-size:.9rem}
          .hu-features__grid{grid-template-columns:1fr}
          .hu-programmes__grid{grid-template-columns:1fr}
          .hu-cta__inner{padding:2rem 1.25rem}
          .hu-footer__grid{grid-template-columns:1fr}
          .hu-footer__bottom{flex-direction:column;text-align:center}
          .hu-partners__logos{gap:1.5rem}
          .hu-partners__logo-item img{height:36px}
          .hu-programmes__header{flex-direction:column;align-items:flex-start;gap:.75rem;margin-bottom:2rem}
          h2.hu-section-title{font-size:clamp(1.6rem,6vw,2.2rem)}
        }
        .hu-cta__split{display:grid;grid-template-columns:1fr auto;gap:3rem;align-items:center;text-align:left}
        .hu-cta__split .hu-cta__actions{justify-content:flex-start}
        .hu-cta__split .hu-cta__sub{margin:0 0 2.5rem}
        .hu-cta__img{width:260px;height:260px;border-radius:20px;overflow:hidden;flex-shrink:0;box-shadow:0 24px 64px rgba(27,42,94,.18)}
        .hu-cta__img img{width:100%;height:100%;object-fit:cover;display:block}
        @media(max-width:800px){.hu-cta__split{grid-template-columns:1fr}.hu-cta__img{display:none}}
        .hu-partners{padding:3rem 0;background:#FFF;border-bottom:1px solid var(--navy-border)}
        .hu-partners__label{text-align:center;font-size:.78rem;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:var(--text-muted);margin-bottom:2rem}
        .hu-partners__logos{display:flex;align-items:center;justify-content:center;gap:3rem;flex-wrap:wrap}
        .hu-partners__logo-item{display:flex;flex-direction:column;align-items:center;gap:.5rem;opacity:1;transition:transform 200ms}
        .hu-partners__logo-item:hover{transform:translateY(-2px)}
        .hu-partners__logo-item img{height:52px;width:auto;object-fit:contain;max-width:140px}
        .hu-partners__logo-item span{font-size:.7rem;font-weight:600;color:var(--text-muted);text-align:center;max-width:120px;line-height:1.4}
        .hu-partners__logo-item--dark img{background:rgba(27,42,94,.06);border-radius:8px;padding:.4rem .75rem}
        @media(max-width:680px){.hu-partners__logos{gap:2rem}.hu-partners__logo-item img{height:40px}}
      `}</style>

      <div className="hu-root">
        <LandingNavbar />

        {/* Hero */}
        <section className="hu-hero">
          <img src="/connected-world.svg" className="hu-hero__world" alt="" aria-hidden="true" />
          <div className="hu-container">
            <div className="hu-hero__inner">
              <div>
                <h1 className="hu-hero__headline hu-reveal hu-d1">
                  <span className="line">University Education</span>
                  <span className="line gold-text">Delivered to Your Home</span>
                </h1>
                <p className="hu-hero__sub hu-reveal hu-d2">
                  HomeUni brings world-class education to your fingertips — interactive programmes, live sessions, instant certificates, and a seamless experience built for Ghana's next generation of leaders.
                </p>
                {/* Image shown only on mobile/tablet — between text and buttons */}
                <div className="hu-hero__visual--mobile">
                  <div className="hu-image-frame">
                    <img src="/login-page.jpeg" alt="Students learning online at HomeUni" fetchpriority="high" />
                  </div>
                </div>
                <div className="hu-hero__actions hu-reveal hu-d3">
                  <a href="https://homeunilearn.com/auth/login" target="_blank" rel="noopener noreferrer" className="hu-btn hu-btn--gold hu-btn--lg"><i className="ri-rocket-line"></i> Get Started</a>
                  <a href="#programmes" className="hu-btn hu-btn--outline hu-btn--lg"><i className="ri-play-circle-line"></i> Browse Programmes</a>
                </div>
                <div className="hu-hero__trust hu-reveal hu-d4">
                  <div className="hu-trust-avatars"><span>KA</span><span>EM</span><span>JO</span><span>AB</span><span>+</span></div>
                  <div className="hu-trust-text">Join a <strong>growing community</strong> of forward-thinking leaders</div>
                </div>
              </div>
              <div className="hu-hero__visual hu-reveal hu-d2">
                <div className="hu-image-frame">
                  <img src="/login-page.jpeg" alt="Students at HomeUni" fetchpriority="high" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Programmes */}
        <section className="hu-programmes" id="programmes">
          <div className="hu-container">
            <div className="hu-programmes__header">
              <div className="hu-reveal">
                <div className="hu-section-label">Top Programmes</div>
                <h2 className="hu-section-title">Programmes built for <span>real careers</span></h2>
              </div>
              <a href="/programmes" className="hu-btn hu-btn--outline hu-reveal">Browse All →</a>
            </div>
            {programmes.length === 0 && (
              <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'1.5rem'}}>
                {[1,2,3,4].map(n => (
                  <div key={n} style={{background:'#FFF',border:'1px solid #E2EEFF',borderRadius:16,overflow:'hidden',height:260}}>
                    <div style={{height:5,background:'#E2EEFF'}} />
                    <div style={{padding:'1.25rem',display:'flex',gap:'.875rem',alignItems:'center'}}>
                      <div style={{width:44,height:44,borderRadius:11,background:'#F1F5F9'}} />
                      <div style={{flex:1}}><div style={{height:10,background:'#F1F5F9',borderRadius:4,marginBottom:6,width:'60%'}} /><div style={{height:8,background:'#F1F5F9',borderRadius:4,width:'40%'}} /></div>
                    </div>
                    <div style={{padding:'0 1.25rem 1.25rem'}}>
                      <div style={{height:12,background:'#F1F5F9',borderRadius:4,marginBottom:8,width:'90%'}} />
                      <div style={{height:8,background:'#F1F5F9',borderRadius:4,marginBottom:6,width:'80%'}} />
                      <div style={{height:8,background:'#F1F5F9',borderRadius:4,width:'70%'}} />
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="hu-programmes__grid">
              {programmes.slice(0,4).map((prog, i) => {
                const accent = PROG_ACCENTS[i % PROG_ACCENTS.length];
                const icon = PROG_ICONS[i % PROG_ICONS.length];
                return (
                  <div key={prog.programme_id} className="hu-prog-card" onClick={() => navigate(`/programmes/${prog.name.toLowerCase().replace(/&/g,'and').replace(/[^a-z0-9\s-]/g,'').trim().replace(/\s+/g,'-').replace(/-+/g,'-')}`)}>
                    <div className="hu-prog-card__accent" style={{background: accent}} />
                    <div className="hu-prog-card__header">
                      <div className="hu-prog-card__icon-wrap" style={{background:`${accent}18`}}>
                        <i className={icon} style={{color:accent}} />
                      </div>
                      <div className="hu-prog-card__meta-top">
                        {prog.code && <div className="hu-prog-card__code" style={{color:accent}}>{prog.code}</div>}
                        {prog.level && <div className="hu-prog-card__level" style={{background:`${accent}15`,color:accent}}>{prog.level}</div>}
                      </div>
                    </div>
                    <div className="hu-prog-card__body">
                      <div className="hu-prog-card__title">{prog.name}</div>
                      <div className="hu-prog-card__desc">{prog.description}</div>
                      <div className="hu-prog-card__stats">
                        {prog.duration && <span><i className="ri-time-line" />{prog.duration}</span>}
                        <span><i className="ri-book-open-line" />{prog.course_count || 0} courses</span>
                      </div>
                      <div className="hu-prog-card__footer">
                        <div className="hu-prog-card__price" style={{color:accent}}>GHS {Number(prog.price||0).toLocaleString()}</div>
                        <button className="hu-prog-card__cta" style={{background:accent}} onClick={e => { e.stopPropagation(); navigate(`/programmes/${prog.name.toLowerCase().replace(/&/g,'and').replace(/[^a-z0-9\s-]/g,'').trim().replace(/\s+/g,'-').replace(/-+/g,'-')}`); }}>Enrol Now</button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Partners */}
        <section className="hu-partners">
          <div className="hu-container">
            <div className="hu-partners__label">Our Partners &amp; Accrediting Bodies</div>
            <div className="hu-partners__logos">
              {[
                {src:'/partners/ugc_logo-300x165.png',                  alt:'University of Gold Coast',             name:'University of Gold Coast',             dark:false},
                {src:'/partners/CMI.png',                                alt:'CMI – Chartered Management Institute', name:'CMI – Chartered Management Institute', dark:false},
                {src:'/partners/ncc_education-logo_white-1-300x86.png', alt:'NCC Education / NCCA',                 name:'NCC Education / NCCA',                 dark:true},
                {src:'/partners/ATHE-400x250.webp',                     alt:'ATHE – Award for Training and Higher Education', name:'ATHE',                        dark:false},
              ].map((p,i) => (
                <div key={i} className={`hu-partners__logo-item${p.dark ? ' hu-partners__logo-item--dark' : ''}`}>
                  <img src={p.src} alt={p.alt} />
                  <span>{p.name}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="hu-features" id="features">
          <div className="hu-container">
            <div className="hu-text-center hu-reveal">
              <div className="hu-section-label">Platform Features</div>
              <h2 className="hu-section-title">Everything you need to <span>learn and grow</span></h2>
              <p className="hu-section-sub">A fully integrated learning ecosystem — from enrolment to certification, all in one place.</p>
            </div>
            <div className="hu-features__grid">
              {[
                {mod:'hu-feat-card--highlight',icon:'ri-play-circle-line',title:'Interactive Programmes',text:'Structured learning paths with video lessons, rich text content, and PDFs — all accessible on any device.'},
                {mod:'hu-feat-card--b',icon:'ri-live-line',title:'Live Sessions',text:'Join scheduled live classes with your facilitators. Get real-time notifications and never miss a session that matters.'},
                {mod:'hu-feat-card--c',icon:'ri-medal-line',title:'Verified Certificates',text:'Complete a programme and receive a digitally signed, verifiable certificate — downloadable as a beautiful PDF instantly.'},
                {mod:'hu-feat-card--d',icon:'ri-questionnaire-line',title:'Quizzes & Assessments',text:'Auto-graded MCQ, true/false, and multi-select quizzes. Assignment submissions graded by your programme manager with rich feedback.'},
                {mod:'hu-feat-card--e',icon:'ri-wallet-3-line',title:'Integrated Wallet',text:'Pay Online to top up your wallet, enrol in paid programmes, and track every transaction — all within the platform in Ghana Cedis.'},
                {mod:'hu-feat-card--f',icon:'ri-discuss-line',title:'Forum & Chat',text:'Engage with peers and staff in the community forum. Send direct messages to collaborators with real-time polling chat.'},
              ].map((f,i) => (
                <div key={i} className={`hu-feat-card ${f.mod} hu-reveal hu-d${(i%3)+1}`}>
                  <div className="hu-feat-card__icon"><i className={f.icon}></i></div>
                  <div className="hu-feat-card__title">{f.title}</div>
                  <div className="hu-feat-card__text">{f.text}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="hu-how" id="how-it-works">
          <div className="hu-container">
            <div className="hu-how__inner">
              <div>
                <div className="hu-reveal">
                  <div className="hu-section-label">How It Works</div>
                  <h2 className="hu-section-title">From sign-up to <span>certificate</span> in four steps</h2>
                  <p className="hu-section-sub">Getting started is simple. Your entire learning journey happens in one seamless flow.</p>
                </div>
                <div className="hu-how__steps">
                  {[
                    {n:'01',t:'Create your account',d:'Register with your email, verify your account, and complete a quick onboarding to personalise your experience.'},
                    {n:'02',t:'Browse & enrol',d:'Explore published programmes across disciplines. Enrol for free or top up your wallet to access premium courses.'},
                    {n:'03',t:'Learn at your pace',d:'Work through modules, attend live sessions, submit assignments, and take quizzes on your schedule — from any device.'},
                    {n:'04',t:'Earn your certificate',d:'Complete the programme and download your verified certificate — shareable and instantly verifiable by anyone online.'},
                  ].map((s,i) => (
                    <div key={i} className={`hu-step hu-reveal hu-d${i+1}`}>
                      <div className="hu-step__num">{s.n}</div>
                      <div><div className="hu-step__title">{s.t}</div><div className="hu-step__text">{s.d}</div></div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="hu-how__visual hu-reveal hu-d2">
                <div style={{borderRadius:'20px',overflow:'hidden',boxShadow:'0 32px 80px rgba(27,42,94,.18)',border:'1px solid var(--navy-border)',lineHeight:0}}>
                  <img src="/feature-image-1.jpeg" alt="HomeUni platform in action" style={{width:'100%',height:'auto',display:'block',objectFit:'cover'}} />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Campus visual banner */}
        <section style={{padding:0,overflow:'hidden',lineHeight:0}}>
          <img src="/hu-banner-1.jpeg" alt="HomeUni campus"
            style={{width:'100%',height:'360px',objectFit:'cover',objectPosition:'center',display:'block'}} />
        </section>

        {/* Testimonials */}
        <section className="hu-testimonials">
          <div className="hu-container">
            <div className="hu-text-center hu-reveal">
              <div className="hu-section-label">Student Stories</div>
              <h2 className="hu-section-title">Words from our <span>learners</span></h2>
              <p className="hu-section-sub">Real experiences from students at the HomeUni.</p>
            </div>
            <div className="hu-testimonials__grid">
              {[
                {init:'KA',name:'Kwame Asante',role:'ATHE Level 6 Extended Diploma in Management',half:false,q:'HomeUni transformed how I approach learning. The live sessions with real lecturers and the ability to revisit content at my own pace made all the difference in my results.'},
                {init:'EO',name:'Esi Owusu',role:'Executive Development Certificate · Strategic Finance & Investment Leadership',half:false,q:'The quiz system is fantastic — immediate feedback helps me understand where I need to improve. I earned two certificates and my employer was genuinely impressed.'},
                {init:'JA',name:'Joseph Agyei',role:'Executive Development Certificate · Strategic HRM & People Leadership',half:true,q:"As a working professional, being able to study on my schedule was everything. HomeUni's seamless mobile experience and expert-led content made it so easy to keep going."},
              ].map((t,i) => (
                <div key={i} className={`hu-testi-card hu-reveal hu-d${i+1}`}>
                  <div className="hu-testi-card__quote">"</div>
                  <div className="hu-testi-card__text">{t.q}</div>
                  <div className="hu-testi-card__author">
                    <div className="hu-testi-card__avatar">{t.init}</div>
                    <div><div className="hu-testi-card__name">{t.name}</div><div className="hu-testi-card__role">{t.role}</div></div>
                    <div className="hu-testi-card__stars">
                      {[1,2,3,4].map(n=><i key={n} className="ri-star-fill"></i>)}
                      <i className={t.half ? 'ri-star-half-fill' : 'ri-star-fill'}></i>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Banner */}
        <section className="hu-cta">
          <div className="hu-container">
            <div className="hu-cta__inner hu-reveal">
              <div className="hu-cta__split">
                <div>
                  <h2 className="hu-cta__title">Ready to start your <span>learning journey?</span></h2>
                  <p className="hu-cta__sub">Join thousands of students at the HomeUni building the careers they deserve — one lesson at a time.</p>
                  <div className="hu-cta__actions">
                    <a href="https://homeunilearn.com/auth/login" target="_blank" rel="noopener noreferrer" className="hu-btn hu-btn--gold hu-btn--lg"><i className="ri-rocket-line"></i> Create Your Account</a>
                    <a href="https://homeunilearn.com/auth/login" target="_blank" rel="noopener noreferrer" className="hu-btn hu-btn--outline hu-btn--lg">Sign In to Dashboard →</a>
                  </div>
                  <p className="hu-cta__note">
                    <i className="ri-checkbox-circle-fill"></i> Accredited programmes &nbsp;&nbsp;
                    <i className="ri-checkbox-circle-fill"></i> Expert facilitators &nbsp;&nbsp;
                    <i className="ri-checkbox-circle-fill"></i> Verified certificates
                  </p>
                </div>
                <div className="hu-cta__img">
                  <img src="/hu-login-1.png" alt="Student on HomeUni platform" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="hu-footer">
          <div className="hu-container">
            <div className="hu-footer__grid">
              <div>
                <a href="/"><img src="/main-logo-horinzontal.png" alt="HomeUni Logo" style={{height:'48px',width:'auto',objectFit:'contain',filter:'drop-shadow(0 2px 8px rgba(21,101,192,0.2))'}}/></a>
                <p className="hu-footer__brand-text">The official online learning management system of the HomeUni. Empowering students and staff with modern education tools.</p>
                <div className="hu-footer__socials">
                  {['ri-twitter-x-line','ri-linkedin-fill','ri-facebook-fill','ri-instagram-line'].map((ic,i)=>(
                    <a key={i} href="#" className="hu-footer__social"><i className={ic}></i></a>
                  ))}
                </div>
              </div>
              <div>
                <div className="hu-footer__col-title">Platform</div>
                <ul className="hu-footer__links">
                  <li><a href="#features">Features</a></li><li><a href="#programmes">Programmes</a></li>
                  <li><a href="#how-it-works">How It Works</a></li><li><a href="#roles">For Educators</a></li>
                  <li><a href="/api/docs/">API Docs</a></li>
                </ul>
              </div>
              <div>
                <div className="hu-footer__col-title">University</div>
                <ul className="hu-footer__links">
                  <li><a href="#">About UGC</a></li><li><a href="#">Faculties</a></li>
                  <li><a href="#">Admissions</a></li><li><a href="#">Research</a></li><li><a href="#">News &amp; Events</a></li>
                </ul>
              </div>
              <div>
                <div className="hu-footer__col-title">Support</div>
                <ul className="hu-footer__links">
                  <li><a href="#">Help Centre</a></li><li><a href="#">Contact Us</a></li>
                  <li><a href="#">Privacy Policy</a></li><li><a href="#">Terms of Use</a></li>
                  <li><a href="https://panel.homeunilearn.com/auth/login" target="_blank" rel="noopener noreferrer">Staff Login</a></li>
                </ul>
              </div>
            </div>
            <div className="hu-footer__bottom">
              <div className="hu-footer__copy">&copy; {new Date().getFullYear()} <span>HomeUni</span>. All rights reserved. Built with HomeUni.</div>
              <div className="hu-footer__bottom-links"><a href="#">Privacy</a><a href="#">Terms</a><a href="#">Cookies</a></div>
            </div>
          </div>
        </footer>
      </div>
      {showBackTop && (
        <button className="hu-back-top" onClick={() => window.scrollTo({top:0,behavior:'smooth'})} aria-label="Back to top">
          <i className="ri-arrow-up-line" />
        </button>
      )}
    </>
  );
}
